import React, { useRef } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { formatPrice } from '../lib/currency';
import { Star, MapPin, Clock, ArrowRight, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

// A single curated discovery card (demo/preview inventory). Clicking it runs a real
// in-app search for the item's destination (Hotels/Experiences), so all downstream CTAs
// still route through the central /api/go attribution engine. We NEVER present indicative
// demo pricing as a live bookable price.
export const DiscoveryCard = ({ item, currency, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect?.(item)}
    className="group text-left w-64 shrink-0 rounded-2xl border border-border bg-card overflow-hidden card-elevate focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none"
    data-testid={`discovery-card-${item.id}`}
  >
    <div className="h-36 relative overflow-hidden">
      <img src={item.image} alt={item.title} loading="lazy"
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
      {item.is_demo && (
        <span className="absolute top-2 left-2 text-[10px] font-medium bg-primary/85 text-primary-foreground px-1.5 py-0.5 rounded" data-testid="discovery-demo-badge">Demo · preview</span>
      )}
      {(item.category || item.property_type) && (
        <Badge className="absolute top-2 right-2 bg-card/90 text-foreground border-border rounded-full text-[10px]">{item.category || item.property_type}</Badge>
      )}
    </div>
    <div className="p-3">
      <h4 className="font-semibold text-sm leading-snug line-clamp-2">{item.title}</h4>
      <p className="mt-0.5 text-xs text-muted-foreground flex items-center gap-1">
        <MapPin className="h-3 w-3" /> {item.location || item.destination}
      </p>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        {item.rating != null && (
          <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {item.rating}</span>
        )}
        {item.duration_hours != null && (
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {item.duration_hours}h</span>
        )}
        {item.stars != null && (
          <span className="inline-flex items-center gap-0.5">{Array.from({ length: item.stars }).map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}</span>
        )}
      </div>
      <div className="mt-2 flex items-end justify-between">
        <div>
          {item.indicative_from_inr != null && (
            <>
              <p className="text-[10px] text-muted-foreground leading-none">{item.is_live ? 'from' : 'from · indicative'}</p>
              <p className="text-base font-semibold tabular-nums">{formatPrice(item.indicative_from_inr, currency)}</p>
            </>
          )}
        </div>
        <span className="text-xs font-medium text-accent inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-[gap]">
          Explore <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  </button>
);

// Horizontal scrolling collection row. Includes prev/next buttons for pointer devices
// (touch users can swipe; buttons appear from md+ where swiping isn't available).
export const DiscoveryCollection = ({ collection, currency, onSelect }) => {
  const scroller = useRef(null);
  const scrollBy = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: 'smooth' });
  };
  return (
    <section className="mb-8" data-testid={`discovery-collection-${collection.key}`}>
      <div className="flex items-baseline justify-between mb-3 gap-3">
        <div>
          <h3 className="font-display text-xl font-semibold tracking-tight">{collection.title}</h3>
          {collection.subtitle && <p className="text-sm text-muted-foreground">{collection.subtitle}</p>}
        </div>
        <div className="hidden md:flex items-center gap-1.5 shrink-0 self-center">
          <button type="button" onClick={() => scrollBy(-1)} aria-label="Scroll left"
            data-testid={`discovery-scroll-prev-${collection.key}`}
            className="h-9 w-9 grid place-items-center rounded-full border border-border bg-card hover:border-accent hover:text-accent transition-colors focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => scrollBy(1)} aria-label="Scroll right"
            data-testid={`discovery-scroll-next-${collection.key}`}
            className="h-9 w-9 grid place-items-center rounded-full border border-border bg-card hover:border-accent hover:text-accent transition-colors focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div ref={scroller} className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1 scroll-smooth">
        {collection.items.map((it) => (
          <DiscoveryCard key={it.id} item={it} currency={currency} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
};

// "Popular destinations" tiles (India + International).
export const DestinationTiles = ({ destinations, onSelect, title = 'Popular destinations' }) => {
  const domestic = destinations.filter((d) => d.scope === 'domestic');
  const intl = destinations.filter((d) => d.scope !== 'domestic');
  const Group = ({ label, list }) => (
    <div className="mb-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {list.map((d) => (
          <button key={d.slug} type="button" onClick={() => onSelect?.(d)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:border-accent hover:text-accent transition-colors focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none"
            data-testid={`destination-chip-${d.slug}`}>
            <MapPin className="h-3.5 w-3.5" /> {d.name}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <section className="mb-8" data-testid="discovery-destinations">
      <h3 className="font-display text-xl font-semibold tracking-tight mb-3 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-accent" /> {title}
      </h3>
      {domestic.length > 0 && <Group label="Popular in India" list={domestic} />}
      {intl.length > 0 && <Group label="International" list={intl} />}
    </section>
  );
};
